import { useApi, useMessage } from 'app/context';
import { IMedicalCenterDeleteSchema, IMedicalCenterSchema, IMedicalCenterUpdate } from 'generated/axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from './useForm';
import { buildErrorDetails, buildErrorMessage } from 'app/utils';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';

export const useMedicalCenterDetail = (id: string) => {
  const { medicalCenterApi } = useApi();
  const { error, success } = useMessage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const [detail, setDetail] = useState<IMedicalCenterSchema>();

  const [request, setRequest] = useState<IMedicalCenterUpdate>();

  const buildRequestFromDetail = (detail: IMedicalCenterSchema): IMedicalCenterUpdate => ({
    name: detail.name,
    address: detail.address,
    user_email: detail.user_email,
    phone: detail.phone,
    vat_number: detail.vat_number,
    language: detail.language,
  });

  //GET
  const get = useCallback(() => {
    setLoading(true);
    medicalCenterApi
      .adminGetMedicalCenterByID(id)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        setDetail(resp.data);
        setRequest(buildRequestFromDetail(resp.data));
      })
      .catch((err) => error({ message: buildErrorMessage(err) }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicalCenterApi, id]);

  useEffect(() => {
    get();
  }, [get]);

  //UPDATE
  const update = useCallback(
    (form: IMedicalCenterUpdate) => {
      setLoading(true);
      medicalCenterApi
        .adminUpdateMedicalCenter(id, form)
        .then((resp) => {
          if (resp.status >= 400) {
            return Promise.reject(resp);
          }
          success({ message: t('medicalCenter.updateSuccess') as string });
          setDetail(resp.data);
          setRequest(buildRequestFromDetail(resp.data));
        })
        .catch((err) => {
          error({ message: buildErrorMessage(err) });
          updateForm.setErrors(buildErrorDetails(err));
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [medicalCenterApi, id],
  );

  //FORM
  const updateForm = useForm<IMedicalCenterUpdate>({
    defaultValues: request,
    onSubmit: update,
  });

  //ACTIVATE
  const activate = useCallback(() => {
    setLoading(true);
    medicalCenterApi
      .adminActivateMedicalCenter(id)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        success({ message: t('medicalCenter.activateSuccess') as string });
        setDetail(resp.data);
        setRequest(buildRequestFromDetail(resp.data));
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicalCenterApi, id]);

  //DEACTIVATE
  const deactivate = useCallback(() => {
    setLoading(true);
    medicalCenterApi
      .adminDectivateMedicalCenter(id)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        success({ message: t('medicalCenter.deactivateSuccess') as string });
        setDetail(resp.data);
        setRequest(buildRequestFromDetail(resp.data));
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicalCenterApi, id]);

  //DELETE
  const deleteMedicalCenter = useCallback(
    (form: IMedicalCenterDeleteSchema) => {
      setLoading(true);
      medicalCenterApi
        .adminDeleteMedicalCenter(id, form)
        .then((resp) => {
          if (resp.status >= 400) {
            return Promise.reject(resp);
          }
          success({ message: t('medicalCenter.deleteSuccess') as string });
          navigate(-1);
        })
        .catch((err) => {
          error({ message: buildErrorMessage(err) });
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [medicalCenterApi, id],
  );

  return { detail, loading, updateForm, update, activate, deactivate, deleteMedicalCenter };
};
